/**
 * Mock catalog for assessment “Add questions” focus flow —
 * Library bank, past assessments, and import staging.
 *
 * The bank is deliberately large (~1.2K rows) so the pool exercises pagination
 * and the selection mix readout at realistic scale. Curated rows come first —
 * they carry the hand-written stems the blueprint gaps and import preview rely
 * on — followed by deterministically generated rows.
 */

import { DEFAULT_EXAM_CATEGORIES } from "@/lib/exam-categories/defaults"
import { MOCK_FACULTY } from "@/lib/mock/exam-data"
import { NAV_USER } from "@/lib/mock/navigation"
import { CATALOG_BANK_QUESTIONS } from "@/lib/mock/question-catalog"
import type { QuestionRevision } from "@/lib/question-preview"
import type { QuestionBankStatus } from "@/lib/question-bank-status"

export type { QuestionBankStatus }
export {
  QUESTION_BANK_STATUSES,
  QUESTION_BANK_STATUS_ICON,
  QUESTION_BANK_STATUS_TONE,
  normalizeQuestionBankStatus,
} from "@/lib/question-bank-status"

export type AddQuestionSource = "library" | "past" | "import" | "create"

/** Revised Bloom's taxonomy, ordered lowest to highest cognitive demand. */
export type BloomLevel =
  "Remember" | "Understand" | "Apply" | "Analyze" | "Evaluate" | "Create"

export const BLOOM_LEVELS: readonly BloomLevel[] = [
  "Remember",
  "Understand",
  "Apply",
  "Analyze",
  "Evaluate",
  "Create",
]

/**
 * One glyph per level, so a Bloom's row is recognisable before it is read.
 *
 * Each is the verb, not a rank: recalling is a brain, understanding is the
 * lightbulb going on, applying runs the machinery, analysing takes the data
 * apart, evaluating weighs it, creating builds something new. The ladder itself
 * is carried by row order and by `BLOOM_DEMAND`, because six icons cannot say
 * "higher than" (P10, and §8.6 Case A — every one of these sits beside its
 * label).
 */
export const BLOOM_ICON: Record<BloomLevel, string> = {
  Remember: "fa-brain",
  Understand: "fa-lightbulb",
  Apply: "fa-gears",
  Analyze: "fa-magnifying-glass-chart",
  Evaluate: "fa-scale-balanced",
  Create: "fa-pen-ruler",
}

/** Questions the bank never had tagged for Bloom's. */
export const BLOOM_UNTAGGED_ICON = "fa-circle-dashed"

/** Bank row before an author is stamped on it (see `withAuthor`). */
interface SeedQuestion {
  id: string
  /**
   * Short name authors scan the bank by — the stem is the question a student
   * reads, this is what a colleague calls it in conversation.
   */
  title: string
  stem: string
  type: string
  topic: string
  difficulty: "Easy" | "Medium" | "Hard"
  /**
   * `false` when the source row carries no difficulty tag and `difficulty`
   * above is only a value the grid can sort and draw bars for.
   *
   * Bank rows are always tagged, so this is absent on them and absence means
   * tagged. Rows adapted from a paper roster or the library may not be, and a
   * panel that reads the fallback as a claim tells the author their untagged
   * question was tagged Medium.
   */
  difficultyTagged?: boolean
  sourceKind: "library" | "past" | "import"
  sourceLabel: string
  folderId: string
  /**
   * All filing locations. When absent, the question lives only in `folderId`.
   * Composer multi-select writes both: `folderId` is the primary (first) location.
   */
  folderIds?: string[]
  pbi: string
  points?: number
  /** Absent on questions the bank has never had tagged — a real gap authors care about. */
  bloom?: BloomLevel
  /**
   * Publish state. Ready can sit on a live paper; Draft is still being written.
   * Same vocabulary as assessment structure (`QuestionRecord.status`).
   * Optional on seeds — absence means Ready once stamped for the pool.
   */
  status?: QuestionBankStatus
  /**
   * When an imported row landed in the pool (ms). Used to sort Imported
   * newest-first and to show the Imported date column.
   */
  importedAt?: number
  /**
   * Extraction triage flags at the moment the row entered the pool. Direct
   * import stamps drafts without a review pass, so Duplicate / Needs attention
   * have to travel with the row — live import-store state is already settled.
   */
  importSignals?: {
    duplicate: boolean
    needsAttention: boolean
  }
  /**
   * Session-new highlight for DataTable (`bg-dt-new-row-bg` + blue dot).
   * Standing bank rows omit this.
   */
  isNew?: boolean
}

/** Filing locations for a bank row — multi-select or the legacy single `folderId`. */
export function bankQuestionFolderIds(row: {
  folderId: string
  folderIds?: string[]
}): string[] {
  return row.folderIds?.length ? row.folderIds : [row.folderId]
}

export interface AssessmentBankQuestion
  extends SeedQuestion, Record<string, unknown> {
  /**
   * Who wrote it. Authors filter the bank by colleague constantly.
   *
   * Optional because not every surface knows. A paper roster row records the
   * question but not who wrote it, and a panel that prints `Unknown` beside a
   * generated avatar has dressed a gap up as a person. Absent means absent, and
   * the surfaces that show a creator omit the line instead.
   */
  author?: string
  authorEmail?: string
  /**
   * Free-text labels the author attached in the composer. The bank's own filing
   * is topic and folder; these are whatever else the author needed to find it
   * by later, so nothing here is a taxonomy the product controls.
   */
  tags?: string[]
  /** Exam category ids. The question sheet edits these; the bank column reads them. */
  categoryIds?: string[]
  /** True when the question has no category. A mapped topic must not fill one in. */
  categoryUnassigned?: boolean
  /**
   * Reuse history, when the surface that built the row has a real one. The
   * bank's own rows do not: their counts are derived in `lib/question-reuse`
   * so the same item reports the same history wherever it is opened. Rows
   * adapted from the library or from a paper carry the recorded numbers here
   * instead, because those surfaces do know.
   */
  timesUsed?: number
  usedInSameCourse?: boolean
  /** Set by adapters for items recorded as never having been sat. */
  neverUsed?: boolean
  /**
   * Revisions written in the app, oldest first. Absent on every seeded row:
   * their history is derived in `lib/question-preview`. A surface that lets an
   * author edit a question appends here, and the newest entry becomes the
   * version the preview serves.
   */
  revisions?: QuestionRevision[]
  /** Ready for a paper, or still a Draft. Always set on bank / pool rows. */
  status: QuestionBankStatus
}

/**
 * Identity for `useRecordStepper`. Module scope so the hook keeps its memo
 * across renders rather than recomputing on every keystroke in the hub.
 */
export const bankQuestionId = (row: AssessmentBankQuestion) => row.id

/**
 * Question authors come from the faculty directory rather than a second
 * hand-written roster, so the person cell shows the same people the rest of the
 * app does.
 *
 * The signed-in user is in the rotation because faculty write questions: a
 * bank where the only author who has never contributed is the person looking at
 * it would make `My questions` a permanently empty room.
 */
const BANK_AUTHORS = [
  ...MOCK_FACULTY.filter((member) => member.status === "Active").map(
    (member) => ({ name: member.name, email: member.email })
  ),
  { name: NAV_USER.name, email: NAV_USER.email },
]

/**
 * Curated + section seeds the signed-in author owns. Explicit so Add questions
 * and the paper roster can demo owner Save vs non-owner Save-as-new without
 * depending on rotation index.
 */
const OWN_QUESTION_IDS = new Set([
  "bank-q2", // angina — Add questions edit / revise tests
  "bank-q4",
  "bank-q5",
  "bank-q7",
  "q-bp-cardio-analyze-1",
  "q-bp-cardio-apply-1",
  "q-bp-cardio-apply-2",
  // Leo case bank rows the sheet and composer demo against
  "ai-case-q1",
  "ai-case-q3",
  "ai-case-q4",
  "ai-case-q12",
  "preview-detail-q1",
])

/** Someone else's items — non-owner may only mint a copy. */
const OTHER_QUESTION_IDS = new Set([
  "bank-q1",
  "bank-q3",
  "bank-q3-fib",
  "bank-q3-hotspot",
  "bank-q3b",
  "bank-q3c",
  "bank-q3d",
  "bank-q3e",
  "bank-q4b",
  "bank-q4c",
  "bank-q6",
  "q-bp-neuro-understand-1",
  "q-bp-neuro-understand-2",
  "q-bp-pharm-remember-1",
  "q-bp-cardio-apply-3",
])

const NAV_AUTHOR = { name: NAV_USER.name, email: NAV_USER.email }

/** First active faculty — never the signed-in user. */
const OTHER_AUTHOR = BANK_AUTHORS.find(
  (member) => member.email !== NAV_USER.email
) ?? BANK_AUTHORS[0]

/** Deterministic so reloads, snapshots, and the pool order all match. */
function withAuthor(row: SeedQuestion, index: number): AssessmentBankQuestion {
  const author = OWN_QUESTION_IDS.has(row.id)
    ? NAV_AUTHOR
    : OTHER_QUESTION_IDS.has(row.id)
      ? OTHER_AUTHOR
      : BANK_AUTHORS[index % BANK_AUTHORS.length]!
  return {
    ...row,
    author: author.name,
    authorEmail: author.email,
    status: row.status ?? "Ready",
  }
}

/**
 * An author for a surface that stores the question but not who wrote it.
 *
 * Mock attribution, same as the rest of this file: the bank stamps faculty on
 * every row it generates, and a paper roster row is the one place that arrives
 * without one. Rather than leave those questions anonymous while every other
 * surface names a person, they draw from the same directory, keyed on the id so
 * a given question keeps its author across reloads.
 *
 * Replace with the real recorded author when the roster carries one.
 */
export function bankAuthorForId(id: string): { name: string; email: string } {
  let hash = 0
  for (let index = 0; index < id.length; index += 1) {
    hash = (hash * 31 + id.charCodeAt(index)) >>> 0
  }
  return BANK_AUTHORS[hash % BANK_AUTHORS.length]
}

/** Ordinal position for `SignalBarsCell` — difficulty is a scale, not a status. */
export const DIFFICULTY_LEVEL: Record<
  AssessmentBankQuestion["difficulty"],
  number
> = {
  Easy: 1,
  Medium: 2,
  Hard: 3,
}

/** Paired with the bar count so the cell still reads without colour. */
export const DIFFICULTY_TONE: Record<
  AssessmentBankQuestion["difficulty"],
  "success" | "warning" | "danger"
> = {
  Easy: "success",
  Medium: "warning",
  Hard: "danger",
}

export const SOURCE_ICON: Record<AssessmentBankQuestion["sourceKind"], string> =
  {
    library: "fa-books",
    past: "fa-clock-rotate-left",
    import: "fa-file-import",
  }

/** Deterministic 0..1 from any string, so a reload never reshuffles a stat. */
export function hashUnit(seed: string): number {
  let hash = 2166136261
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return ((hash >>> 0) % 1000) / 1000
}

/**
 * The upper-lower discrimination index D: the pass rate of the top third of a
 * cohort minus the pass rate of the bottom third.
 *
 * Derived from PBI rather than seeded separately, because the two measure the
 * same property — whether the item separates strong examinees from weak ones —
 * with different arithmetic. In real data they stay very close; the derivation
 * keeps that correlation and adds only a tight deterministic spread.
 *
 * `null` means the item has never been sat, which is a different statement from
 * a low score.
 */
export const DISCRIMINATION_NEAR_PBI_SPREAD = 0.06

export function discriminationNearPbi(pbi: number, seed = ""): number {
  const spread = seed
    ? (hashUnit(`${seed}:d`) - 0.5) * DISCRIMINATION_NEAR_PBI_SPREAD
    : 0.02
  return Math.min(0.95, Math.max(0, Number((pbi + spread).toFixed(2))))
}

export function discriminationIndex(
  row: Pick<AssessmentBankQuestion, "id" | "pbi">
): number | null {
  const pbi = Number(row.pbi)
  if (Number.isNaN(pbi)) return null
  return discriminationNearPbi(pbi, row.id)
}

export interface AssessmentBlueprintGap {
  /** Exam category domain id (level 1), e.g. `cardiology`. */
  categoryId: string
  need: string
  severity: "high" | "medium"
  targetCount: number
  difficulty: AssessmentBankQuestion["difficulty"]
  questionType: string
  /**
   * Cognitive demand the plan asks for. A blueprint that names only topic and
   * type says nothing about whether the paper tests recall or judgement, which
   * is the one thing an accreditation review asks about — so coverage groups on
   * it, and `matchesGap` counts a question against a target only when the level
   * agrees.
   */
  bloom: BloomLevel
}

/**
 * Bank folders are a real two-level tree (subject > sub-folder), mirroring the
 * question bank. `topic` stays the subject name so blueprint gaps keep matching
 * on it while folders carry the finer grain.
 */
export interface AssessmentBankFolder {
  id: string
  name: string
  parentId: string | null
}

/**
 * A document already attached to this section, offered as something to read
 * questions out of.
 *
 * The size and page count are here because the import picker shows them, and
 * it shows them because reading a document costs the author two to five
 * minutes. A reading pack and a question paper are equally
 * plausible until you know one is 38 pages and the other is 12, which is the
 * fact that decides which one to spend the wait on.
 */
export interface AssessmentReference {
  id: string
  name: string
  /** Uppercase extension, shown as-is. Derived at authoring time, not parsed. */
  kind: "PDF" | "DOCX" | "DOC" | "CSV" | "XLSX"
  /** Already formatted, since nothing computes against it. */
  size: string
  /** Absent for a spreadsheet, where a page count would be a fiction. */
  pages?: number
}

export interface AssessmentAddContext {
  courseCode: string
  courseName: string
  assessmentTitle: string
  sectionTitle: string
  /**
   * Root folders this course teaches from. The bank is the nursing program.
   * NURS-210 draws on its categories first; the rest of the program stays
   * behind All questions.
   */
  courseFolderIds: string[]
  references: AssessmentReference[]
  blueprintGaps: AssessmentBlueprintGap[]
  pastAssessments: { id: string; name: string; year: string }[]
}

/**
 * Ids are prefixed `bank-` on purpose. Section rosters (`SECTION_SEED`) number
 * their questions `q1`, `q2`, … to match the assessment structure page, and
 * membership is keyed by id: an unprefixed `q2` here made an unrelated
 * Cardiology question read as already on the section.
 */
const CURATED_QUESTIONS: SeedQuestion[] = [
  {
    id: "bank-q1",
    title: "Heart failure nursing priority",
    stem: "A patient with new heart failure reports dyspnea. Which nursing assessment is the priority?",
    type: "Multiple Choice",
    points: 1,
    topic: "Cardiology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.35",
    bloom: "Understand",
  },
  {
    id: "bank-q2",
    title: "Angina symptoms",
    stem: "Which symptom of angina should the nurse teach a patient to report?",
    type: "Multiple Choice",
    points: 1,
    topic: "Cardiology",
    difficulty: "Easy",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.45",
    bloom: "Remember",
  },
  {
    id: "bank-q3",
    title: "Cardiac drug mechanisms",
    stem: "Match each cardiac medication to the teaching point the nurse should give.",
    type: "Match the following",
    points: 1,
    topic: "Cardiology",
    difficulty: "Hard",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.22",
    bloom: "Analyze",
  },
  {
    id: "bank-q3-fib",
    title: "Facial nerve blank",
    stem: "The _________ nerve should the nurse associate with motor control of facial expression.",
    type: "Fill in the blank",
    points: 1,
    topic: "Neurology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Neurology",
    folderId: "f2",
    pbi: "0.38",
    bloom: "Remember",
  },
  {
    id: "bank-q3-hotspot",
    title: "SA node on conduction diagram",
    stem: "Select the region on the conduction system diagram that initiates the cardiac impulse.",
    type: "Hotspot",
    points: 1,
    topic: "Cardiology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.41",
    bloom: "Apply",
    status: "Draft",
  },
  {
    id: "bank-q3b",
    title: "SA node function",
    stem: "A telemetry strip is regular at 72/min with a P wave before each QRS. Which structure is setting this rate?",
    type: "Multiple Choice",
    points: 1,
    topic: "Cardiology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.40",
    bloom: "Remember",
  },
  {
    id: "bank-q3c",
    title: "Leading cause of MI",
    stem: "Which cause should the nurse recognize as the most common trigger of myocardial infarction?",
    type: "Multiple Choice",
    points: 1,
    topic: "Cardiology",
    difficulty: "Easy",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.50",
    bloom: "Remember",
  },
  {
    id: "bank-q3d",
    title: "STEMI vs NSTEMI",
    stem: "How should the nurse explain the difference between STEMI and NSTEMI to a colleague?",
    type: "Essay",
    points: 5,
    topic: "Cardiology",
    difficulty: "Hard",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.20",
    bloom: "Analyze",
  },
  {
    id: "bank-q3e",
    title: "ECG signs of acute STEMI",
    stem: "Which ECG finding should the nurse treat as an acute STEMI?",
    type: "Multiple Choice",
    points: 1,
    topic: "Cardiology",
    difficulty: "Hard",
    sourceKind: "library",
    sourceLabel: "Cardiology",
    folderId: "f1",
    pbi: "0.24",
    bloom: "Apply",
  },
  {
    id: "bank-q4",
    title: "Early Parkinson's signs",
    stem: "Which early finding should the nurse associate with Parkinson disease?",
    type: "Multiple Choice",
    points: 1,
    topic: "Neurology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Neurology",
    folderId: "f2",
    pbi: "0.50",
    bloom: "Remember",
  },
  {
    id: "bank-q4b",
    title: "Frontal lobe function",
    stem: "Which deficit should the nurse anticipate after frontal lobe injury?",
    type: "Multiple Choice",
    points: 1,
    topic: "Neurology",
    difficulty: "Hard",
    sourceKind: "library",
    sourceLabel: "Neurology",
    folderId: "f2",
    pbi: "0.30",
    bloom: "Understand",
  },
  {
    id: "bank-q4c",
    title: "Demyelination and conduction",
    stem: "How should the nurse explain demyelination to a patient with new weakness?",
    type: "Essay",
    points: 5,
    topic: "Neurology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Neurology",
    folderId: "f2",
    pbi: "0.38",
    bloom: "Understand",
  },
  {
    id: "q_path1",
    title: "Apoptosis definition",
    stem: "Which finding should the nurse associate with programmed cell death?",
    type: "Multiple Choice",
    points: 1,
    topic: "Pathology",
    difficulty: "Easy",
    sourceKind: "library",
    sourceLabel: "Pathology",
    folderId: "f3",
    pbi: "0.60",
    bloom: "Remember",
  },
  {
    id: "q_path2",
    title: "Stages of necrosis",
    stem: "Which tissue change should the nurse recognize as necrosis?",
    type: "Multiple Choice",
    points: 1,
    topic: "Pathology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Pathology",
    folderId: "f3",
    pbi: "0.40",
    bloom: "Remember",
  },
  {
    id: "q_pharm1",
    title: "Aspirin mechanism",
    stem: "How should the nurse explain the action of aspirin to a patient?",
    type: "Multiple Choice",
    points: 1,
    topic: "Pharmacology",
    difficulty: "Medium",
    sourceKind: "library",
    sourceLabel: "Pharmacology",
    folderId: "f4",
    pbi: "0.55",
    bloom: "Understand",
  },
  {
    id: "q_pharm2",
    title: "Fastest route of administration",
    stem: "Which route should the nurse expect to produce the fastest systemic effect?",
    type: "Multiple Choice",
    points: 1,
    topic: "Pharmacology",
    difficulty: "Easy",
    sourceKind: "library",
    sourceLabel: "Pharmacology",
    folderId: "f4",
    pbi: "0.61",
    bloom: "Remember",
  },
  {
    id: "bank-q5",
    title: "Mitochondrial function",
    stem: "Which cellular finding should the nurse associate with mitochondrial failure?",
    type: "Multiple Choice",
    points: 1,
    topic: "Cell Biology",
    difficulty: "Easy",
    sourceKind: "past",
    sourceLabel: "NURS-210 Midterm 2024",
    folderId: "pa1",
    pbi: "0.80",
    bloom: "Remember",
  },
  {
    id: "bank-q6",
    title: "Cellular respiration",
    stem: "How should the nurse explain why a hypoxic patient cannot produce enough ATP?",
    type: "Essay",
    points: 5,
    topic: "Cell Biology",
    difficulty: "Hard",
    sourceKind: "past",
    sourceLabel: "NURS-210 Midterm 2024",
    folderId: "pa1",
    pbi: "0.40",
    bloom: "Understand",
  },
  {
    id: "bank-q7",
    title: "Early sepsis warning signs",
    stem: "List two early warning signs of sepsis the nurse should escalate.",
    type: "Short Answer",
    points: 2,
    topic: "Clinical",
    difficulty: "Medium",
    sourceKind: "past",
    sourceLabel: "NURS-210 Medication Quiz",
    folderId: "pa2",
    pbi: "0.55",
    bloom: "Remember",
  },
  {
    // Unsat library item — preview shows em dashes for item analysis. Not an
    // import: Imported starts empty until the author reads a document.
    id: "bank-q-unsat",
    title: "Nursing process first step",
    stem: "Which action is the first step of the nursing process?",
    type: "Multiple Choice",
    points: 1,
    topic: "General",
    difficulty: "Easy",
    sourceKind: "library",
    sourceLabel: "General",
    folderId: "f1",
    pbi: "--",
    bloom: "Remember",
  },
]

// ─── Generated bank ──────────────────────────────────────────────────────────

interface TopicSeedChild {
  id: string
  name: string
  subjects: string[]
}

interface TopicSeed {
  topic: string
  folderId: string
  children: TopicSeedChild[]
}

/**
 * Blueprint topic stored on a question, keyed by exam-category domain id.
 * Folder labels use the category name. The topic stays the short name the
 * section plan already checks.
 */
const BLUEPRINT_TOPIC_BY_CATEGORY: Record<string, string> = {
  cardiology: "Cardiology",
  neurology: "Neurology",
  pulmonology: "Physiology",
  pharmacology: "Pharmacology",
  "diagnostic-reasoning": "Diagnostic Reasoning",
  "laboratory-interpretation": "Pathology",
  "patient-safety": "Patient Safety",
  "professionalism-ethics": "Professionalism and Ethics",
  "patient-communication": "Patient Communication",
  "emergency-recognition": "Emergency Recognition",
  pediatrics: "Pediatrics",
  "womens-health": "Women's Health",
  endocrinology: "Endocrinology",
  "infectious-disease": "Infectious Disease",
}

/** Older bank topics rewritten onto the nursing catalog. */
const NURSING_TOPIC: Record<string, string> = {
  "Cell Biology": "Pathology",
  Clinical: "Diagnostic Reasoning",
  Musculoskeletal: "Diagnostic Reasoning",
  General: "Diagnostic Reasoning",
  Renal: "Pathology",
  Microbiology: "Infectious Disease",
  Immunology: "Infectious Disease",
  Respiratory: "Physiology",
  "Clinical Reasoning": "Diagnostic Reasoning",
}

/** Older folder ids, before the bank used exam category ids. */
const LEGACY_FOLDER_ROOT: Record<string, string> = {
  f1: "cardiology",
  f2: "neurology",
  f3: "laboratory-interpretation",
  f4: "pharmacology",
  f5: "laboratory-interpretation",
  f6: "diagnostic-reasoning",
  f7: "infectious-disease",
  f8: "infectious-disease",
  f9: "endocrinology",
  f10: "pulmonology",
  f11: "laboratory-interpretation",
  f12: "diagnostic-reasoning",
  "f-reasoning": "diagnostic-reasoning",
  "f-reasoning-a": "diagnostic-reasoning",
}

const NURSING_SUBJECTS: Record<string, string[]> = {
  cardiology: ["atrial fibrillation", "heart failure", "chest pain"],
  neurology: ["stroke recognition", "seizure precautions", "cranial nerve assessment"],
  pulmonology: ["acute dyspnea", "oxygen titration", "pneumonia"],
  pharmacology: ["warfarin monitoring", "opioid safety", "antibiotic selection"],
  "diagnostic-reasoning": ["sepsis recognition", "next diagnostic step", "problem representation"],
  "laboratory-interpretation": ["troponin trend", "electrolyte imbalance", "acid-base change"],
  "patient-safety": ["fall risk", "medication error", "two patient identifiers"],
  "professionalism-ethics": ["informed consent", "confidentiality", "documentation"],
  "patient-communication": ["teach-back", "discharge teaching", "difficult news"],
  "emergency-recognition": ["unstable blood pressure", "anaphylaxis", "time-critical chest pain"],
  pediatrics: ["pediatric fever", "growth concerns", "age-specific dosing"],
  "womens-health": ["prenatal warning signs", "contraception counseling", "postpartum assessment"],
  endocrinology: ["insulin resistance", "hypoglycemia", "thyroid storm"],
  "infectious-disease": ["gram-negative sepsis", "microbiology culture results", "isolation precautions"],
}

const CATEGORY_ROOTS = DEFAULT_EXAM_CATEGORIES.filter(
  (category) => category.parentId === null,
)

function blueprintTopicForCategory(categoryId: string): string {
  return BLUEPRINT_TOPIC_BY_CATEGORY[categoryId] ?? categoryId
}

/**
 * Subject folders follow the nursing exam categories: a domain, then its skills.
 * `topic` stays the short blueprint name so section alignment still matches.
 */
const TOPIC_SEEDS: TopicSeed[] = CATEGORY_ROOTS.map((root) => {
  const skills = DEFAULT_EXAM_CATEGORIES.filter(
    (category) => category.parentId === root.id,
  )
  const subjects = NURSING_SUBJECTS[root.id] ?? ["nursing assessment"]
  return {
    topic: blueprintTopicForCategory(root.id),
    folderId: root.id,
    children: skills.map((skill) => ({
      id: skill.id,
      name: skill.name,
      subjects,
    })),
  }
})

/**
 * Categories the NURS-210 midterm still lists, even when the bank files some
 * of them under another course.
 */
const CS101_FOLDER_IDS = [
  "cardiology",
  "neurology",
  "pharmacology",
  "laboratory-interpretation",
  "diagnostic-reasoning",
  "pulmonology",
  "emergency-recognition",
  "patient-communication",
]

/** Course is the parent. Category, then sub-category, sit under it. */
const BANK_COURSES: { id: string; name: string; categoryIds: readonly string[] }[] = [
  {
    id: "course-nurs-120",
    name: "NURS-120 Fundamentals of Nursing",
    categoryIds: ["patient-safety", "patient-communication"],
  },
  {
    id: "course-nurs-210",
    name: "NURS-210 Clinical Sciences I",
    categoryIds: [
      "cardiology",
      "pulmonology",
      "emergency-recognition",
      "diagnostic-reasoning",
    ],
  },
  {
    id: "course-nurs-211",
    name: "NURS-211 Clinical Sciences II",
    categoryIds: ["neurology"],
  },
  {
    id: "course-nurs-230",
    name: "NURS-230 Health Assessment",
    categoryIds: ["laboratory-interpretation"],
  },
  {
    id: "course-nurs-320",
    name: "NURS-320 Pharmacology",
    categoryIds: ["pharmacology"],
  },
  {
    id: "course-nurs-330",
    name: "NURS-330 Adult Health Nursing I",
    categoryIds: ["endocrinology", "infectious-disease"],
  },
  {
    id: "course-nurs-350",
    name: "NURS-350 Pediatric Nursing",
    categoryIds: ["pediatrics"],
  },
  {
    id: "course-nurs-410",
    name: "NURS-410 Community Health Nursing",
    categoryIds: ["professionalism-ethics", "womens-health"],
  },
]

const COURSE_ID_BY_CATEGORY = new Map<string, string>(
  BANK_COURSES.flatMap((course) =>
    course.categoryIds.map((categoryId) => [categoryId, course.id] as const),
  ),
)

const CATEGORY_ROOT_IDS = new Set(CATEGORY_ROOTS.map((category) => category.id))

export const ASSESSMENT_BANK_FOLDERS: AssessmentBankFolder[] = [
  ...BANK_COURSES.map((course) => ({
    id: course.id,
    name: course.name,
    parentId: null,
  })),
  ...TOPIC_SEEDS.flatMap((seed) => {
    const root = CATEGORY_ROOTS.find((category) => category.id === seed.folderId)
    const courseId = COURSE_ID_BY_CATEGORY.get(seed.folderId) ?? "course-nurs-210"
    return [
      {
        id: seed.folderId,
        name: root?.name ?? seed.topic,
        parentId: courseId,
      },
      ...seed.children.map((child) => ({
        id: child.id,
        name: child.name,
        parentId: seed.folderId,
      })),
    ]
  }),
]

/** Category under the course, not the course itself. Topic stays the blueprint name. */
export function blueprintTopicForFolder(folderId: string): string {
  const byId = new Map(ASSESSMENT_BANK_FOLDERS.map((folder) => [folder.id, folder]))
  let node = byId.get(folderId)
  while (node && !CATEGORY_ROOT_IDS.has(node.id) && node.parentId) {
    node = byId.get(node.parentId)
  }
  return blueprintTopicForCategory(node?.id ?? folderId)
}

function categoryIdForBlueprintTopic(topic: string): string | undefined {
  return Object.entries(BLUEPRINT_TOPIC_BY_CATEGORY).find(
    ([, name]) => name === topic,
  )?.[0]
}

function fileNursingQuestion<
  T extends { folderId: string; topic: string; sourceKind?: string },
>(row: T): T {
  const topic = NURSING_TOPIC[row.topic] ?? row.topic
  if (row.sourceKind === "past") return { ...row, topic }
  if (NURSING_TOPIC[row.topic]) {
    return {
      ...row,
      topic,
      folderId: categoryIdForBlueprintTopic(topic) ?? row.folderId,
    }
  }
  const legacy = LEGACY_FOLDER_ROOT[row.folderId]
  if (!legacy) return { ...row, topic }
  return { ...row, folderId: legacy, topic: blueprintTopicForCategory(legacy) }
}

/**
 * The title says what the question asks *about* the subject, so a folder of 80
 * rows reads as a list of distinct items rather than 80 variations of one stem.
 */
const TITLE_ANGLES: Record<BloomLevel, string> = {
  Remember: "expected finding",
  Understand: "client teaching",
  Apply: "priority action",
  Analyze: "assessment finding",
  Evaluate: "care plan",
  Create: "monitoring plan",
}

const STEM_TEMPLATES: Record<BloomLevel, (subject: string) => string> = {
  Remember: (subject) =>
    `A client is admitted with ${subject}. Which assessment finding is expected?`,
  Understand: (subject) =>
    `A client asks why ${subject} matters. Which explanation is accurate?`,
  Apply: (subject) =>
    `A client presents with ${subject}. Which action is the priority?`,
  Analyze: (subject) =>
    `Which assessment finding best distinguishes ${subject} from a similar presentation?`,
  Evaluate: (subject) =>
    `A client with ${subject} has conflicting orders. Which plan is most appropriate?`,
  Create: (subject) =>
    `A client is at risk for ${subject}. Which monitoring plan is indicated?`,
}

/** Bloom's levels weighted by difficulty — harder questions skew to higher demand. */
const BLOOM_BY_DIFFICULTY: Record<
  AssessmentBankQuestion["difficulty"],
  BloomLevel[]
> = {
  Easy: [
    "Remember",
    "Remember",
    "Remember",
    "Understand",
    "Understand",
    "Apply",
  ],
  Medium: ["Understand", "Understand", "Apply", "Apply", "Apply", "Analyze"],
  Hard: ["Apply", "Analyze", "Analyze", "Evaluate", "Evaluate", "Create"],
}

const DIFFICULTIES: AssessmentBankQuestion["difficulty"][] = [
  "Easy",
  "Easy",
  "Medium",
  "Medium",
  "Medium",
  "Hard",
  "Hard",
]

const QUESTION_TYPES = [
  "Multiple Choice",
  "Multiple Choice",
  "Multiple Choice",
  "Multiple Choice",
  "Multiple Select",
  "True or False",
  "Short Answer",
  "Essay",
  "Fill in the blank",
  "Match the following",
  "Hotspot",
]

/**
 * The three papers with hand-authored shapes in `course-objectives.ts`, which
 * the Adapt flow reads. Their ids and names are fixed by that file.
 */
const CURATED_PAST_ASSESSMENTS = [
  { id: "pa2", name: "NURS-210 Medication Quiz", year: "2025" },
  { id: "pa3", name: "NURS-210 Final 2024", year: "2024" },
  { id: "pa1", name: "NURS-210 Midterm 2024", year: "2024" },
]

/** A term's sittings, latest first, so a year reads newest to oldest. */
const ARCHIVED_PAPER_TITLES = ["Final", "Medication Quiz 2", "Midterm", "Medication Quiz"]

/**
 * Everything before 2024.
 *
 * A course that has run for a dozen years has fifty-odd papers behind it, and
 * the navigator has to hold up under that: three papers was a fixture that made
 * the rail look easier than the job is. They are generated rather than written
 * out because nothing about an eleven-year-old quiz is interesting except that
 * it exists and is in the way.
 */
function archivedPastAssessments() {
  const papers: { id: string; name: string; year: string }[] = []
  for (let year = 2023; year >= 2012; year -= 1) {
    for (const title of ARCHIVED_PAPER_TITLES) {
      papers.push({
        id: `pa-${year}-${title.toLowerCase().replace(/\s+/g, "-")}`,
        name: `NURS-210 ${title} ${year}`,
        year: String(year),
      })
    }
  }
  return papers
}

/** Newest first, which is the order the rail and the Location tree both use. */
const PAST_ASSESSMENTS = [
  ...CURATED_PAST_ASSESSMENTS,
  ...archivedPastAssessments(),
]

const PAST_ASSESSMENT_LABELS = PAST_ASSESSMENTS.map((paper) => ({
  label: paper.name,
  folderId: paper.id,
}))

/** Linear congruential generator — deterministic so every render and reload matches. */
function makeRng(seed: number) {
  let state = seed >>> 0
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0
    return state / 4294967296
  }
}

function pick<T>(rng: () => number, items: readonly T[]): T {
  return items[Math.floor(rng() * items.length)]
}

/**
 * Which paper a past question came off, weighted hard toward the recent ones.
 *
 * A bank holds dozens of questions off last year's final and one or two off a
 * quiz from a decade ago: papers get retired, and the ones nobody reuses stop
 * accumulating. Flat odds across fifty papers would have given every year the
 * same weight and made the tail of the list look worth reading.
 */
function pickPastPaper(rng: () => number) {
  const roll = rng()
  const index = Math.floor(roll * roll * roll * PAST_ASSESSMENT_LABELS.length)
  return PAST_ASSESSMENT_LABELS[index]
}

function generateBankQuestions(perTopic: number): AssessmentBankQuestion[] {
  const rng = makeRng(20260726)
  const rows: AssessmentBankQuestion[] = []

  TOPIC_SEEDS.forEach((seed, topicIndex) => {
    for (let index = 0; index < perTopic; index += 1) {
      const difficulty = pick(rng, DIFFICULTIES)
      const bloomRoll = rng()
      const bloom =
        bloomRoll < 0.06
          ? undefined
          : pick(rng, BLOOM_BY_DIFFICULTY[difficulty])
      const child = pick(rng, seed.children)
      const subject = pick(rng, child.subjects)
      // A past NURS-210 paper can only hold subjects NURS-210 teaches, so subjects
      // owned by other courses never sit under "Past assessments".
      const isPast = rng() < 0.18 && CS101_FOLDER_IDS.includes(seed.folderId)
      const past = pickPastPaper(rng)
      const hasPbi = rng() > 0.22
      const author = pick(rng, BANK_AUTHORS)

      const angle = TITLE_ANGLES[bloom ?? "Understand"]

      rows.push({
        id: `gq-${topicIndex}-${index}`,
        title: `${subject[0].toUpperCase()}${subject.slice(1)}: ${angle}`,
        stem: STEM_TEMPLATES[bloom ?? "Understand"](subject),
        type: pick(rng, QUESTION_TYPES),
        topic: seed.topic,
        difficulty,
        sourceKind: isPast ? "past" : "library",
        sourceLabel: isPast ? past.label : child.name,
        folderId: isPast ? past.folderId : child.id,
        pbi: hasPbi ? (0.12 + rng() * 0.62).toFixed(2) : "--",
        points: 1 + Math.floor(rng() * 4),
        bloom,
        author: author.name,
        authorEmail: author.email,
        // A small draft share so Status is filterable without flooding the pool.
        status: rng() < 0.08 ? "Draft" : "Ready",
      })
    }
  })

  return rows
}

export const ASSESSMENT_BANK_QUESTIONS: AssessmentBankQuestion[] = [
  // The catalog questions lead, because they are the ones the library
  // lists and the assessment paper is built from. They used to be absent
  // here entirely: an author could read CASE-2026-003 in the bank, open
  // Add questions to put it on a paper, and not find it.
  ...CATALOG_BANK_QUESTIONS.map((row) => {
    // `bankAuthorForId` answers {name, email}; the row field names are
    // `author` and `authorEmail`. Spreading it raw left every catalog
    // row with no author, so the pool printed a blank column and the
    // preview panel had nobody to name.
    const author = bankAuthorForId(row.id)
    return { ...row, author: author.name, authorEmail: author.email }
  }) as unknown as AssessmentBankQuestion[],
  ...CURATED_QUESTIONS.map((row, index) =>
    fileNursingQuestion(withAuthor(row, index)),
  ),
  ...generateBankQuestions(100),
]

/**
 * Questions already on each section when the author opens Add questions —
 * same roster as assessment structure.
 *
 * Seeds are aligned to `blueprintGaps` on purpose: Blueprint coverage is
 * assessment-wide and matches category + Bloom + difficulty + type. A roster of
 * Cell Biology / Clinical items against Cardiology / Neurology targets made
 * every gap read 0 of N while the paper looked full — which is a lie.
 */
const SECTION_SEED: Record<string, SeedQuestion[]> = {
  A: [
    // Cardiology · Analyze · Hard MCQ — need 2, seed 1 (under)
    {
      id: "q-bp-cardio-analyze-1",
      title: "STEMI ECG progression",
      stem: "A patient presents with crushing chest pain. Which ECG finding should the nurse treat as an acute STEMI needing immediate reperfusion?",
      type: "Multiple Choice",
      topic: "Cardiology",
      difficulty: "Hard",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.42",
      points: 5,
      bloom: "Analyze",
    },
    // Cardiology · Apply · Medium MCQ — need 3; A holds 3, B adds 2 more → over
    {
      id: "q-bp-cardio-apply-1",
      title: "Heart failure diuretic choice",
      stem: "Which diuretic should the nurse expect first for volume overload in acute decompensated heart failure?",
      type: "Multiple Choice",
      topic: "Cardiology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.38",
      points: 5,
      bloom: "Apply",
    },
    {
      id: "q-bp-cardio-apply-2",
      title: "AF rate control agent",
      stem: "For a hemodynamically stable patient with new atrial fibrillation and rapid ventricular response, which agent should the nurse anticipate for rate control?",
      type: "Multiple Choice",
      topic: "Cardiology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.35",
      points: 5,
      bloom: "Apply",
    },
    {
      id: "q-bp-cardio-apply-3",
      title: "ACS antiplatelet loading",
      stem: "Which antiplatelet regimen should the nurse prepare before PCI in NSTE-ACS?",
      type: "Multiple Choice",
      topic: "Cardiology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.40",
      points: 5,
      bloom: "Apply",
    },
    // Neurology · Understand · Medium MCQ — need 2, seed 1 (under)
    {
      id: "q-bp-neuro-understand-1",
      title: "Stroke territory signs",
      stem: "Which clinical finding should the nurse treat as an MCA territory ischemic stroke?",
      type: "Multiple Choice",
      topic: "Neurology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Neurology",
      folderId: "f2",
      pbi: "0.33",
      points: 5,
      bloom: "Understand",
    },
    // Pharmacology · Remember · Easy MCQ — need 1, seed 1 (met)
    {
      id: "q-bp-pharm-remember-1",
      title: "Beta blocker primary action",
      stem: "Which receptor should the nurse recall when a beta blocker lowers heart rate?",
      type: "Multiple Choice",
      topic: "Pharmacology",
      difficulty: "Easy",
      sourceKind: "library",
      sourceLabel: "Pharmacology",
      folderId: "f4",
      pbi: "0.45",
      points: 5,
      bloom: "Remember",
    },
  ],
  B: [
    // Extra Cardiology Apply Medium → pushes that target over (3 in A + 2 here = 5 of 3)
    {
      id: "q-bp-cardio-apply-4",
      title: "Hypertensive urgency oral agent",
      stem: "Which oral agent should the nurse anticipate for gradual blood-pressure reduction in hypertensive urgency without end-organ damage?",
      type: "Multiple Choice",
      topic: "Cardiology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.30",
      points: 5,
      bloom: "Apply",
    },
    {
      id: "q-bp-cardio-apply-5",
      title: "Statin intensity after MI",
      stem: "After an uncomplicated MI, which statin intensity should the nurse expect for secondary prevention?",
      type: "Multiple Choice",
      topic: "Cardiology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.36",
      points: 5,
      bloom: "Apply",
    },
    // Neurology Understand — second of two (met when combined with A)
    {
      id: "q-bp-neuro-understand-2",
      title: "Parkinson motor triad",
      stem: "Which triad should the nurse recognize as idiopathic Parkinson disease?",
      type: "Multiple Choice",
      topic: "Neurology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Neurology",
      folderId: "f2",
      pbi: "0.41",
      points: 5,
      bloom: "Understand",
    },
  ],
  C: [
    // Cardiology · Evaluate · Hard Essay — need 1 (met)
    {
      id: "q-bp-cardio-essay-1",
      title: "HFpEF pathophysiology essay",
      stem: "How should the nurse explain heart failure with preserved ejection fraction and justify one evidence-based treatment approach?",
      type: "Essay",
      topic: "Cardiology",
      difficulty: "Hard",
      sourceKind: "library",
      sourceLabel: "Cardiology",
      folderId: "f1",
      pbi: "0.55",
      points: 15,
      bloom: "Evaluate",
    },
    // Pharmacology · Analyze · Medium Short Answer — need 2, seed 1 (under)
    {
      id: "q-bp-pharm-sa-1",
      title: "Warfarin interaction rationale",
      stem: "Why should the nurse expect INR to rise when a CYP2C9 inhibitor is added to steady-dose warfarin?",
      type: "Short Answer",
      topic: "Pharmacology",
      difficulty: "Medium",
      sourceKind: "library",
      sourceLabel: "Pharmacology",
      folderId: "f4",
      pbi: "0.34",
      points: 8,
      bloom: "Analyze",
    },
  ],
}

/** Roster already on a section when Add questions opens — Mix measures this. */
export function sectionQuestionsAlreadyAdded(
  sectionId: string
): AssessmentBankQuestion[] {
  const seeds = SECTION_SEED[sectionId] ?? []
  return seeds.map((row, index) =>
    fileNursingQuestion(withAuthor(row, index + 40)),
  )
}

export interface AssessmentSectionSummary {
  id: string
  title: string
}

/**
 * Every section of the assessment being built, in paper order.
 *
 * Add questions is scoped to one of them, but Mix reports on all three: an
 * author judging whether section A came out too hard is really asking how it
 * compares with B and C, and that comparison is unanswerable from inside one
 * section. Structure view reads the same list, so the titles cannot drift.
 */
export const ASSESSMENT_SECTIONS: readonly AssessmentSectionSummary[] = [
  { id: "A", title: "Cardiovascular Conditions" },
  { id: "B", title: "Pharmacology and Therapeutics" },
  { id: "C", title: "Diagnostic Reasoning" },
]

export const ASSESSMENT_ADD_CONTEXT: AssessmentAddContext = {
  courseCode: "NURS-210",
  courseName: "Clinical Sciences I",
  assessmentTitle: "Clinical Sciences I Midterm",
  sectionTitle: "Cardiovascular Conditions",
  // NURS-210 teaches these categories. Pediatrics, women's health, endocrine
  // conditions, and infectious disease stay in the program bank.
  courseFolderIds: CS101_FOLDER_IDS,
  /**
   * One document, and it is the paper the course actually sat: a long list of
   * invented references read as filler and put twenty decisions in front of
   * the one that matters. The file is
   * `public/mock-docs/cs-101-nursing-midterm-1.html`, and
   * `question-import-extraction.ts` reads exactly what it says.
   */
  references: [
    {
      id: "upload:NURS-210 Nursing Midterm 1 question paper.docx",
      name: "NURS-210 Nursing Midterm 1 question paper.docx",
      kind: "DOCX",
      size: "1.2 MB",
      pages: 8,
    },
  ],
  blueprintGaps: [
    {
      categoryId: "cardiology-recognize-cardiovascular-conditions",
      need: "2 Hard MCQ",
      severity: "high",
      targetCount: 2,
      difficulty: "Hard",
      questionType: "Multiple Choice",
      bloom: "Analyze",
    },
    {
      categoryId: "cardiology-diagnose-cardiovascular-disease",
      need: "3 Medium MCQ",
      severity: "medium",
      targetCount: 3,
      difficulty: "Medium",
      questionType: "Multiple Choice",
      bloom: "Apply",
    },
    {
      categoryId: "cardiology-treat-cardiovascular-disease",
      need: "1 Hard Essay",
      severity: "medium",
      targetCount: 1,
      difficulty: "Hard",
      questionType: "Essay",
      bloom: "Evaluate",
    },
    {
      categoryId: "neurology-recognize-neurologic-condition",
      need: "2 Medium MCQ",
      severity: "medium",
      targetCount: 2,
      difficulty: "Medium",
      questionType: "Multiple Choice",
      bloom: "Understand",
    },
    {
      categoryId: "neurology-localize-neurologic-lesion",
      need: "1 Medium Essay",
      severity: "medium",
      targetCount: 1,
      difficulty: "Medium",
      questionType: "Essay",
      bloom: "Apply",
    },
    {
      categoryId: "pharmacology-select-the-appropriate-medication",
      need: "1 Easy MCQ",
      severity: "medium",
      targetCount: 1,
      difficulty: "Easy",
      questionType: "Multiple Choice",
      bloom: "Remember",
    },
    {
      categoryId: "pharmacology-understand-drug-mechanism",
      need: "2 Medium short answers",
      severity: "medium",
      targetCount: 2,
      difficulty: "Medium",
      questionType: "Short Answer",
      bloom: "Analyze",
    },
  ],
  /**
   * Every paper `PAST_ASSESSMENT_LABELS` files questions under, listed newest
   * first: within one year the order here is what breaks the tie, and a final
   * comes after a midterm. Leaving one out stranded its questions in a row the
   * navigator could not name.
   */
  pastAssessments: PAST_ASSESSMENTS,
}

export function questionsForSource(
  source: Exclude<AddQuestionSource, "create">
): AssessmentBankQuestion[] {
  return ASSESSMENT_BANK_QUESTIONS.filter((q) => q.sourceKind === source)
}

export function parseAddQuestionSource(
  value: string | null | undefined
): AddQuestionSource {
  if (
    value === "past" ||
    value === "import" ||
    value === "create" ||
    value === "library"
  ) {
    return value
  }
  return "library"
}
